import { createClient, type SupabaseClient } from '@supabase/supabase-js';

/**
 * Supabase-klient i browseren.
 *
 * Den findes udelukkende for at gennemfoere OAuth-haandtrykket med Microsoft. Alt andet
 * gaar gennem API'et, som ejer sessionen i HTTP-only cookies - se lib/api.ts.
 *
 * Kun anon-noeglen hoerer hjemme her. Den er offentlig af design og beskyttet af RLS.
 * Service-role-noeglen maa aldrig faa et VITE_-praefiks: alt med det praefiks bliver
 * bagt ind i bundtet og udleveret til enhver der aabner siden.
 */

const url = import.meta.env['VITE_SUPABASE_URL'] as string | undefined;
const anonKey = import.meta.env['VITE_SUPABASE_ANON_KEY'] as string | undefined;

/** Sandt naar Microsoft-login kan tilbydes. Login-siden skjuler knappen naar det er falsk. */
export const isOAuthConfigured = Boolean(url && anonKey);

/**
 * Eksplicit noegle frem for den udledte default. Vi rydder selv op bagefter, og til det
 * skal vi kende noeglen praecist - se clearBrowserSession.
 */
const STORAGE_KEY = 'codepass-oauth';

let cached: SupabaseClient | null = null;

export function supabaseBrowserClient(): SupabaseClient {
  if (!url || !anonKey) {
    throw new Error(
      'Microsoft-login er ikke konfigureret. Sæt VITE_SUPABASE_URL og VITE_SUPABASE_ANON_KEY i .env.',
    );
  }

  /**
   * pkce frem for implicit: koden kommer tilbage som en query-parameter og byttes med en
   * verifier, saa selve tokenet aldrig staar i URL'en - hverken i browserhistorik eller i
   * en Referer-header.
   *
   * detectSessionInUrl er slaaet fra, saa udvekslingen sker ét sted vi selv styrer
   * (AuthCallback) frem for som en bivirkning af at klienten bliver oprettet.
   */
  cached ??= createClient(url, anonKey, {
    auth: {
      storageKey: STORAGE_KEY,
      flowType: 'pkce',
      detectSessionInUrl: false,
      // Skal vaere true: pkce-verifieren gemmes her mellem redirect og tilbagekomst.
      // AuthCallback rydder op igen, saa snart tokenet er byttet til en cookie.
      persistSession: true,
      autoRefreshToken: false,
    },
  });

  return cached;
}

/**
 * Rydder den lokale Supabase-session, naar tokenet er afleveret til API'et og ligger
 * sikkert i en HTTP-only cookie.
 *
 * Bevidst en direkte oprydning i localStorage frem for auth.signOut(). signOut kan naa
 * auth-serveren og tilbagekalde praecis den session, API'et lige har lagt i cookien - og
 * saa er brugeren logget ud i samme sekund som ind, uden at noget ser forkert ud
 * undervejs. Her er der ingen netvaerkskald og dermed intet at tilbagekalde.
 *
 * Verifieren ryddes med: den er brugt op, saa snart koden er byttet.
 */
export function clearBrowserSession(): void {
  window.localStorage.removeItem(STORAGE_KEY);
  window.localStorage.removeItem(`${STORAGE_KEY}-code-verifier`);
}
